import React from 'react';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import useMountTransition from '../../../../../hooks/useMountTransition';
import store from '../../../../../store';
import { schedulerActions } from '../../../../../store/schedulerSlice';
import { isChild } from '../../../../../utils/dom';
import StageButton from './StageButton';

interface IButtonList {
  stageIndex: number;
}

const Stage = ({ stageIndex }: IButtonList) => {
  const stage = useAppSelector(
    (global) => global.schedulerSlice.stageData?.[stageIndex],
  );

  const isMounted = !!stage?.name;
  const hasTransitionedIn = useMountTransition(isMounted, 1000);

  const [ref, setRef] = React.useState<HTMLElement | null>(null);
  const focusStage = useAppSelector((global) => {
    return global.schedulerSlice.focusStage;
  });

  const doFocus = React.useCallback(() => {
    if (!stage || stage.name === 'daysList') return;

    if (!ref) return;
    const firstBtn = ref.querySelector(
      '.stage_btn:not(:disabled)',
    ) as HTMLElement;
    if (firstBtn) {
      firstBtn.focus();
    }
  }, [ref, stage]);

  React.useEffect(() => {
    if (focusStage === stage?.name) doFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStage]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(doFocus, [ref, stage]);

  const handleFocus = React.useCallback(
    (sum: number) => {
      const focusedElement = document.activeElement;
      if (
        isChild(focusedElement as HTMLElement, (current) => current === ref) &&
        ref
      ) {
        const buttons = [
          ...ref.querySelectorAll<HTMLElement>('.stage_btn:not(:disabled)'),
        ];
        if (buttons) {
          const index = buttons.findIndex(
            (current) => current === focusedElement,
          );

          if (buttons[index] === focusedElement && buttons[index - sum]) {
            buttons[index - sum].focus();

            store.dispatch(
              schedulerActions.setState({ focusStage: stage?.name }),
            );
          }
        }
      }
    },
    [ref, stage?.name],
  );

  const handleKeyDown = React.useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key.toLowerCase() === 'arrowdown') {
        ev.preventDefault();
        handleFocus(-1);
      }
      if (ev.key.toLowerCase() === 'arrowup') {
        ev.preventDefault();
        handleFocus(+1);
      }
    },
    [handleFocus],
  );

  return stage ? (
    <Box
      data-name={stage?.name}
      className={`stage ${hasTransitionedIn && 'in'} ${isMounted && 'visible'}`}
      ref={setRef}
      onKeyDown={handleKeyDown}
    >
      {(hasTransitionedIn || isMounted) &&
        stage?.buttons?.map((elem) => {
          return <StageButton button={elem} key={elem.title} />;
        })}
    </Box>
  ) : null;
};

export default Stage;
